import { useState } from 'react'
import { Box, Typography } from '@mui/material'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import ImageNotSupportedOutlinedIcon from '@mui/icons-material/ImageNotSupportedOutlined'
import { LOT_STATUS } from '../utils/constants'

const STATUS_BADGE = {
  [LOT_STATUS.ACTUAL]: { label: 'Actual', color: '#004B8D' },
  [LOT_STATUS.SIN_POSTOR]: { label: 'Sin postor', color: '#78909C' },
  [LOT_STATUS.RETIRADO]: { label: 'Retirado', color: '#C62828' },
  [LOT_STATUS.VENDIDO]: { label: 'Vendido', color: '#2E7D32' },
}

const HIGHLIGHT_SX = {
  current: { background: '#56AF31', borderColor: '#428625' },
  selected: (theme) => ({ borderColor: theme.palette.primary.dark, boxShadow: `0 0 0 3px ${theme.palette.primary.dark}` }),
  'right-even': { background: 'linear-gradient(135deg, #7AB2E1 0%, #5C9FD9 100%)', borderColor: '#4A90D9' },
  'right-odd': { background: 'linear-gradient(135deg, #004B8D 0%, #1976D2 100%)', borderColor: '#1565C0' },
}

const ON_DARK_HIGHLIGHTS = ['current', 'right-even', 'right-odd']

// Expuesto para que quien arme el contenido de `meta` (precios, oferta, etc.)
// pueda adaptar sus propios colores de texto al mismo fondo que usa la card.
export const isHighlightOnDark = (highlight) => ON_DARK_HIGHLIGHTS.includes(highlight)

/**
 * Tarjeta de lote compartida entre las vistas no-admin: lista de lotes del
 * cliente (AuctionLotItem), selección de lotes con derecho (LotSelectionCard)
 * y el listado de ofertas propias del navbar (ListLotItemOffer). El admin
 * mantiene su propia tarjeta (admin/components/LotItem.js) aparte.
 */
export default function LotCard({
  lot,
  onClick,
  selected,
  disabled,
  isCheckeable,
  highlight,
  layout = 'row',
  meta,
}) {
  const [imageError, setImageError] = useState(false)
  const imageSrc = lot.documents?.[0]?.url
  const badge = STATUS_BADGE[lot.status]
  const isGrid = layout === 'grid'
  const onDark = ON_DARK_HIGHLIGHTS.includes(highlight)
  const highlightSx = highlight ? HIGHLIGHT_SX[highlight] : null

  return (
    <Box
      onClick={!disabled ? onClick : undefined}
      sx={[
        {
          backgroundColor: '#fff',
          border: '1px solid #E3E7EC',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(16, 24, 40, 0.08)',
          cursor: disabled ? 'default' : 'pointer',
          display: 'flex',
          flexDirection: isGrid ? 'column' : 'row',
          flexShrink: 0,
          height: isGrid ? 116 : 'auto',
          minHeight: isGrid ? 0 : 92,
          minWidth: 0,
          opacity: disabled ? 0.55 : 1,
          overflow: 'hidden',
          position: 'relative',
          transition: 'transform 150ms ease, box-shadow 150ms ease, border-color 150ms ease',
          width: '100%',
          '&:hover': disabled ? {} : {
            boxShadow: '0 8px 16px rgba(16, 24, 40, 0.14)',
            transform: 'translateY(-2px)',
          },
        },
        !highlight && selected ? (theme) => HIGHLIGHT_SX.selected(theme) : null,
        typeof highlightSx === 'function' ? (theme) => highlightSx(theme) : highlightSx,
      ]}
    >
      <Box sx={{ flex: 'none', position: 'relative', width: isGrid ? '100%' : 92 }}>
        {imageSrc && !imageError ? (
          <Box
            alt={`Lote ${lot.number}`}
            component="img"
            onError={() => setImageError(true)}
            src={imageSrc}
            sx={{ display: 'block', height: isGrid ? 72 : '100%', minHeight: isGrid ? 0 : 92, objectFit: 'cover', width: '100%' }}
          />
        ) : (
          <Box
            sx={{
              alignItems: 'center',
              bgcolor: '#EEF1F4',
              color: '#AEB7C2',
              display: 'flex',
              height: isGrid ? 72 : '100%',
              justifyContent: 'center',
              minHeight: isGrid ? 0 : 92,
              width: '100%',
            }}
          >
            <ImageNotSupportedOutlinedIcon sx={{ fontSize: 20 }} />
          </Box>
        )}
        {isCheckeable ? (
          <Box
            sx={{
              alignItems: 'center',
              bgcolor: selected ? 'primary.main' : 'rgba(255,255,255,0.9)',
              border: '1.5px solid',
              borderColor: selected ? 'primary.main' : '#C7CFD9',
              borderRadius: '50%',
              display: 'flex',
              height: 17,
              justifyContent: 'center',
              position: 'absolute',
              right: 4,
              top: 4,
              width: 17,
            }}
          >
            {selected ? <CheckRoundedIcon sx={{ color: '#fff', fontSize: 12 }} /> : null}
          </Box>
        ) : null}
        {badge ? (
          <Box
            sx={{
              bgcolor: badge.color,
              borderRadius: 0.5,
              bottom: 4,
              color: '#fff',
              fontSize: 10.5,
              fontWeight: 'bold',
              left: 4,
              letterSpacing: 0.3,
              padding: '2px 7px',
              position: 'absolute',
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
            }}
          >
            {badge.label}
          </Box>
        ) : null}
      </Box>

      <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', justifyContent: 'center', minWidth: 0, p: isGrid ? '6px 8px 8px' : 1 }}>
        <Typography
          sx={{
            color: onDark ? '#fff' : 'primary.main',
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
          }}
        >
          {`Lote ${lot.number}`}
        </Typography>
        <Typography
          noWrap
          sx={{
            color: onDark ? 'rgba(255,255,255,0.9)' : '#475362',
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {lot.name}
        </Typography>
        {meta}
      </Box>
    </Box>
  )
}
